import { loadFont } from "@remotion/google-fonts/Poppins";
import { AbsoluteFill, Img, Sequence, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";

const { fontFamily } = loadFont("normal", { weights: ["500", "600", "700"], subsets: ["latin"] });

/**
 * `ranked-reel` — a 30 second vertical video that counts through a ranked list.
 *
 * Exists because `bookend-reel` has its copy written into the component ("Nobody stays in {city}
 * once"), so it serves exactly one angle and nothing else. Every line here is a prop, which is what
 * lets one composition carry nights, growth, repeat visits or anything else ranked.
 *
 * Thirty seconds rather than eight. The short one was built for a single stat reveal; short form
 * feeds reward watch time and loops, and eight seconds gives a viewer nothing to stay for. This
 * runs hook, then a paced reveal per item, then a payoff.
 *
 * One asset, three destinations: Instagram reels today, TikTok when that account is reachable, and
 * YouTube Shorts. It is deliberately built for the first of those, since that is where the audience
 * actually is.
 *
 * Duration is fixed at 900 frames and the items divide the middle evenly, so three to six items all
 * work without the composition needing `calculateMetadata`. That keeps it independent of the
 * renderer version deployed on the render box.
 */

const FPS = 30;
export const RANKED_REEL_FRAMES = 900; // 30s
const HOOK_END = 100;
const OUTRO_START = 750;

// Instagram paints its own interface over the top and bottom of a reel.
const SAFE_TOP = 120;
const SAFE_BOTTOM = 320;

export type RankedReelItem = {
	name: string;
	/** A string, so "+54%" and "4" both work. Formatting belongs to the caller. */
	value: string;
	/**
	 * Required, not optional. A number and a place name alone leave the frame looking unfinished,
	 * and the eye has three seconds with nothing to do. One short factual line is what makes the
	 * segment worth watching, so the type refuses to let a caller omit it.
	 *
	 * It has to be a fact, not filler. Comparisons hold up well ("Same as Chiang Mai and Phuket"),
	 * and so does anything the per-trip data supports ("38% stop here twice").
	 */
	note: string;
	photo: string;
};

export type RankedReelProps = {
	eyebrow: string;
	hook: string;
	hookPhoto: string;
	items: RankedReelItem[];
	outro: string;
	outroSub?: string;
};

const WASH = "linear-gradient(180deg, rgba(7,31,38,.55) 0%, rgba(7,31,38,.25) 38%, rgba(7,31,38,.9) 100%)";

/** A photo that drifts slowly for the length of its segment, so a still never sits dead on screen. */
const Backdrop: React.FC<{ photo: string; frames: number }> = ({ photo, frames }) => {
	const f = useCurrentFrame();
	const scale = interpolate(f, [0, frames], [1.06, 1.14], { extrapolateRight: "clamp" });
	return (
		<>
			<AbsoluteFill style={{ overflow: "hidden" }}>
				<Img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover", transform: `scale(${scale})` }} />
			</AbsoluteFill>
			<AbsoluteFill style={{ background: WASH }} />
		</>
	);
};

const Hook: React.FC<{ eyebrow: string; hook: string; photo: string }> = ({ eyebrow, hook, photo }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const rise = spring({ frame: f - 6, fps, config: { damping: 200 } });
	const out = interpolate(f, [HOOK_END - 14, HOOK_END], [1, 0], { extrapolateLeft: "clamp" });

	return (
		<AbsoluteFill style={{ opacity: out }}>
			<Backdrop photo={photo} frames={HOOK_END} />
			<AbsoluteFill style={{ padding: `${SAFE_TOP + 60}px 90px ${SAFE_BOTTOM}px`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
				<div style={{ fontSize: 34, fontWeight: 600, letterSpacing: 6, textTransform: "uppercase", color: "#ffc14a", opacity: rise }}>{eyebrow}</div>
				<div
					style={{
						fontSize: 118,
						lineHeight: 1.04,
						fontWeight: 700,
						color: "#fff",
						letterSpacing: -2.5,
						marginTop: 24,
						opacity: rise,
						transform: `translateY(${interpolate(rise, [0, 1], [46, 0])}px)`,
					}}
				>
					{hook}
				</div>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

const Item: React.FC<{ item: RankedReelItem; index: number; total: number; frames: number }> = ({ item, index, total, frames }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const inn = spring({ frame: f - 4, fps, config: { damping: 200 } });
	const num = spring({ frame: f - 12, fps, config: { damping: 12, stiffness: 120 } });
	const out = interpolate(f, [frames - 12, frames], [1, 0], { extrapolateLeft: "clamp" });

	return (
		<AbsoluteFill style={{ opacity: out }}>
			<Backdrop photo={item.photo} frames={frames} />
			<AbsoluteFill style={{ padding: `${SAFE_TOP}px 90px ${SAFE_BOTTOM}px`, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
				{/* Position in the list, so a viewer knows how much is left. Progress is why people stay. */}
				<div style={{ display: "flex", gap: 10, marginBottom: 46 }}>
					{Array.from({ length: total }).map((_, i) => (
						<div key={i} style={{ height: 6, flex: 1, borderRadius: 3, background: i <= index ? "#4fe3b4" : "rgba(255,255,255,.28)" }} />
					))}
				</div>

				<div
					style={{
						fontSize: 230,
						lineHeight: 0.86,
						fontWeight: 700,
						color: "#fff",
						letterSpacing: -8,
						fontVariantNumeric: "tabular-nums",
						opacity: num,
						transform: `scale(${interpolate(num, [0, 1], [0.7, 1])})`,
						transformOrigin: "left bottom",
					}}
				>
					{item.value}
				</div>
				<div
					style={{
						fontSize: 76,
						fontWeight: 600,
						color: "#fff",
						marginTop: 18,
						opacity: inn,
						transform: `translateY(${interpolate(inn, [0, 1], [30, 0])}px)`,
					}}
				>
					{item.name}
				</div>
				<div style={{ fontSize: 40, color: "rgba(255,255,255,.78)", marginTop: 14, opacity: interpolate(f, [18, 30], [0, 1], { extrapolateRight: "clamp" }) }}>
					{item.note}
				</div>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

const Outro: React.FC<{ outro: string; outroSub?: string; photo: string; frames: number }> = ({ outro, outroSub, photo, frames }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const rise = spring({ frame: f - 4, fps, config: { damping: 200 } });
	return (
		<AbsoluteFill>
			<Backdrop photo={photo} frames={frames} />
			<AbsoluteFill style={{ padding: `${SAFE_TOP}px 90px ${SAFE_BOTTOM}px`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
				<div
					style={{
						fontSize: 96,
						lineHeight: 1.08,
						fontWeight: 700,
						color: "#fff",
						letterSpacing: -2,
						opacity: rise,
						transform: `translateY(${interpolate(rise, [0, 1], [34, 0])}px)`,
					}}
				>
					{outro}
				</div>
				{outroSub ? <div style={{ fontSize: 44, color: "rgba(255,255,255,.8)", marginTop: 26, opacity: rise }}>{outroSub}</div> : null}
				<div style={{ fontSize: 36, fontWeight: 600, letterSpacing: 3, color: "#4fe3b4", marginTop: 54, opacity: rise }}>stippl.io</div>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

export const RankedReel: React.FC<RankedReelProps> = ({ eyebrow, hook, hookPhoto, items, outro, outroSub }) => {
	// The middle divides evenly, so the item count can change without the duration changing.
	const span = Math.floor((OUTRO_START - HOOK_END) / Math.max(items.length, 1));

	return (
		<AbsoluteFill style={{ fontFamily, backgroundColor: "#071f26" }}>
			<Sequence durationInFrames={HOOK_END}>
				<Hook eyebrow={eyebrow} hook={hook} photo={hookPhoto} />
			</Sequence>

			{items.map((item, i) => (
				<Sequence key={item.name} from={HOOK_END + i * span} durationInFrames={span}>
					<Item item={item} index={i} total={items.length} frames={span} />
				</Sequence>
			))}

			<Sequence from={OUTRO_START} durationInFrames={RANKED_REEL_FRAMES - OUTRO_START}>
				<Outro outro={outro} outroSub={outroSub} photo={hookPhoto} frames={RANKED_REEL_FRAMES - OUTRO_START} />
			</Sequence>
		</AbsoluteFill>
	);
};
